import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { useDebounce } from "../../../../hooks/use-debounce";
import { ENTITY_TYPE_MAPPER } from "../../../../pages/app/admin/library/DataImport";
import useMedicalCodesImportList from "../../../../queries/use-medical-codes";
import usePatientImportList from "../../../../queries/use-patient-import-list";
import useProviderImportList from "../../../../queries/use-provider-import-list";
import {
  ExportPatientListTypeEnum,
  GetDataImportRecordsTypeEnum,
  MigrationControllerApi,
} from "../../../../services";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getConfigObject,
} from "../../../../utils/utils";
import CustomDataGridV2 from "../../../CustomDataGridV2";
import Flex from "../../../Flex";
import SearchInput from "../../../SearchInput";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";

/** Union of every row shape the import endpoints return for these grids. */
type ImportRecordRow = {
  uuid: string;
  firstName?: string;
  lastName?: string;
  providerName?: string;
  providerType?: string;
  specialities?: string;
  npi?: string;
  email?: string;
  mobileNumber?: string;
  code?: string;
  codeVersion?: string;
  description?: string;
  drugId?: string;
  drugName?: string;
  strength?: string;
  formType?: string;
};

interface ViewRecordDrawerProps {
  opened: boolean;
  onClose: () => void;
  importData: {
    type: ExportPatientListTypeEnum;
    xTENANTID: string;
    uuid: string;
    providerUuid?: string;
  };
}
const ENTITY_RECORD = {
  PROVIDER: "Provider Data",
  CPT: "CPT Code",
  LOINC: "LOINC Code",
  DRUG_CATALOG: "Drug Catalog",
  ICD: "ICD 10 Code",
  HCPCS: "HCPCS Code",
  PATIENT: "Patient Data",
  PAYER_CATALOG: "Payer",
  LAB_TEST: "Lab Test",
  IMAGING_STUDY: "Imaging Study",
} as Record<string, string>;

/**
 * Every medical-code catalog (CPT, HCPCS, LOINC, Payer) is imported as the same
 * code/description pair, so the grid only differs by the code column header.
 */
const getCodeCatalogColumns = (codeHeaderName: string) =>
  applyDefaultColumnSettings<ImportRecordRow>([
    {
      field: "code",
      headerName: codeHeaderName,
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.6,
      width: 240,
      sortable: false,
    },
  ]);

const renderDashFallback: GridColDef<ImportRecordRow>["renderCell"] = ({
  value,
}) => value ?? "-";

const DRUG_CATALOG_COLUMN = applyDefaultColumnSettings<ImportRecordRow>([
  {
    field: "drugId",
    headerName: "Drug ID",
    flex: 0.4,
    width: 150,
    sortable: false,
    renderCell: renderDashFallback,
  },
  {
    field: "drugName",
    headerName: "Drug Name",
    flex: 0.5,
    width: 220,
    sortable: false,
    renderCell: renderDashFallback,
  },
  {
    field: "strength",
    headerName: "Strength",
    flex: 0.4,
    width: 150,
    sortable: false,
    renderCell: renderDashFallback,
  },
  {
    field: "formType",
    headerName: "Form Type",
    flex: 0.4,
    width: 170,
    sortable: false,
    renderCell: renderDashFallback,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 0.8,
    width: 240,
    sortable: false,
    renderCell: renderDashFallback,
  },
]);

const CPT_CODES_COLUMN = getCodeCatalogColumns("CPT Code");
const HCPCS_CODES_COLUMN = getCodeCatalogColumns("HCPCS Code");
const LOINC_CODES_COLUMN = getCodeCatalogColumns("LOINC Code");
const PAYER_CATALOG_COLUMN = getCodeCatalogColumns("Payer Code");

const ViewRecordDrawer = ({
  opened,
  onClose,
  importData,
}: ViewRecordDrawerProps) => {
  const [searchString, setSearch, instantSearchVal] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const searchHandler = useCallback(
    (value: string) => {
      setSearch(value);
      setPagination((prev) => ({ ...prev, page: 0 }));
    },
    [setSearch]
  );

  const { data, isLoading } = usePatientImportList({
    enabled: importData?.type === "PATIENT" && !!importData?.uuid,
    pagination,
    xTENANTID: importData?.xTENANTID,
    dataImportUuid: importData.uuid,
    searchString,
  });

  const { data: medicalCodes, isLoading: isMedicalCodesLoading } =
    useMedicalCodesImportList({
      enabled:
        importData?.type !== "PATIENT" &&
        importData.type !== "PROVIDER" &&
        !!importData?.uuid,
      searchString,
      pagination,
      xTENANTID: importData?.xTENANTID,
      dataImportUuid: importData.uuid,
    });

  const { data: providerImportList, isLoading: isProviderLoading } =
    useProviderImportList({
      searchString,
      enabled: importData?.type === "PROVIDER",
      pagination,
      xTENANTID: importData?.xTENANTID,
      dataImportUuid: importData.uuid,
    });

  const COLUMNS = applyDefaultColumnSettings<ImportRecordRow>([
    {
      field: "uuid",
      headerName: "Patient ID",
      flex: 0.4,
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        return <Text>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>;
      },
    },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "providerName",
      headerName: "Primary Provider Name",
      flex: 0.6,
      width: 240,
      sortable: false,
      renderCell: ({ row }) => row?.providerName ?? "-",
    },
    {
      field: "email",
      headerName: "Email ID",
      flex: 0.6,
      width: 240,
      sortable: false,
    },
    {
      field: "mobileNumber",
      headerName: "Contact Number",
      flex: 0.4,
      width: 170,
      sortable: false,
    },
  ]);

  const ICD_COLUMNS = applyDefaultColumnSettings<ImportRecordRow>([
    {
      field: "codeVersion",
      headerName: "ICD Code Type",
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "code",
      headerName: "ICD Code",
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.6,
      width: 240,
      sortable: false,
    },
  ]);

  // Superseded by PAYER_CATALOG_COLUMN above -- this draft listed patient
  // fields (name, email, contact) that the payer import never returns.
  // const PAYER_COLUMN = applyDefaultColumnSettings<ImportRecordRow>([
  //   { field: "uuid", headerName: "Patient ID", flex: 0.4, width: 150 },
  //   { field: "firstName", headerName: "First Name", flex: 0.5, width: 220 },
  //   { field: "lastName", headerName: "Last Name", flex: 0.5, width: 220 },
  //   { field: "providerName", headerName: "Primary Provider Name", flex: 0.6 },
  //   { field: "email", headerName: "Email ID", flex: 0.6, width: 240 },
  //   { field: "mobileNumber", headerName: "Contact Number", flex: 0.4 },
  // ]);

  const PROVIDER_COLUMN = applyDefaultColumnSettings<ImportRecordRow>([
    {
      field: "uuid",
      headerName: "Provider ID",
      flex: 0.4,
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        return <Text>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>;
      },
    },
    {
      field: "providerType",
      headerName: "Provider Type",
      flex: 0.5,
      width: 220,
      sortable: false,
    },
    {
      field: "providerName",
      headerName: "Provider Name",
      flex: 0.5,
      width: 220,
      sortable: false,
      renderCell: ({ row }) => {
        return <Text>{`${row.firstName ?? ""} ${row.lastName ?? ""}`}</Text>;
      },
    },
    {
      field: "specialities",
      headerName: "Speciality",
      flex: 0.6,
      width: 240,
      sortable: false,
      renderCell: () => "-",
    },
    {
      field: "npi",
      headerName: "NPI Number",
      flex: 0.6,
      width: 240,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email ID",
      flex: 0.4,
      width: 170,
      sortable: false,
    },
  ]);
  const DATA_MAPPER: Record<string, ImportRecordRow[] | undefined> = {
    PATIENT: data?.data?.content,
    PROVIDER: providerImportList?.data?.content,
    CPT: medicalCodes?.data?.content,
    HCPCS: medicalCodes?.data?.content,
    LOINC: medicalCodes?.data?.content,
    PAYER_CATALOG: medicalCodes?.data?.content,
    ICD: medicalCodes?.data?.content,
    DRUG_CATALOG: medicalCodes?.data?.content,
  };

  const TOTAL_RECORDS_MAPPER: Record<string, number | undefined> = {
    PATIENT: data?.data?.page?.totalElements,
    PROVIDER: providerImportList?.data?.page?.totalElements,
    CPT: medicalCodes?.data?.page?.totalElements,
    HCPCS: medicalCodes?.data?.page?.totalElements,
    LOINC: medicalCodes?.data?.page?.totalElements,
    PAYER_CATALOG: medicalCodes?.data?.page?.totalElements,
    ICD: medicalCodes?.data?.page?.totalElements,
    DRUG_CATALOG: medicalCodes?.data?.page?.totalElements,
  };

  const COLUMN_MAPPER: Record<string, GridColDef<ImportRecordRow>[]> = {
    PATIENT: COLUMNS,
    PROVIDER: PROVIDER_COLUMN,
    CPT: CPT_CODES_COLUMN,
    HCPCS: HCPCS_CODES_COLUMN,
    LOINC: LOINC_CODES_COLUMN,
    PAYER_CATALOG: PAYER_CATALOG_COLUMN,
    ICD: ICD_COLUMNS,
    DRUG_CATALOG: DRUG_CATALOG_COLUMN,
  };

  function closeHandler() {
    onClose();
    setSearch("");
    setPagination({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }

  return (
    <SideSheetDrawer
      open={opened}
      color="primary"
      title={
        importData?.type
          ? ENTITY_TYPE_MAPPER[importData.type as GetDataImportRecordsTypeEnum]
          : "-"
      }
      pos="right"
      w={"74vw"}
      onClose={closeHandler}
      endIcon={
        <Button
          startIcon={<FileUploadOutlinedIcon color="primary" />}
          sx={{ width: "9rem" }}
          variant="outlined"
          onClick={() => {
            new MigrationControllerApi(getConfigObject())
              .exportPatientList(
                importData.uuid, //dataImportUuid
                importData.type, //ExportPatientListTypeEnum
                importData.xTENANTID //xTENANTID
              )
              .then((res) => {
                // The endpoint is declared as returning JSON but streams CSV.
                const blobFile = new Blob([res.data as BlobPart], {
                  type: "text/csv",
                });
                const fileUrl = URL.createObjectURL(blobFile);

                const fileName = `${ENTITY_RECORD[importData.type]}.csv`;

                const a = document.createElement("a");
                a.href = fileUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                URL.revokeObjectURL(fileUrl);
              });
          }}
        >
          Export List
        </Button>
      }
    >
      <Flex gap={"10px"} flexDirection={"column"} height={"100%"}>
        <Box width={"48%"}>
          <SearchInput
            searchKey={instantSearchVal}
            onSearch={searchHandler}
            placeholder="Search By Name"
          />
        </Box>

        {importData?.type && COLUMN_MAPPER[importData.type] && (
          <CustomDataGridV2
            rows={importData?.type ? (DATA_MAPPER[importData.type] ?? []) : []}
            getRowId={(row) => row.uuid}
            columns={
              importData?.type ? COLUMN_MAPPER[importData.type] : COLUMNS
            }
            height="calc(100vh - 300px)"
            disableRowSelectionOnClick
            loading={isLoading || isProviderLoading || isMedicalCodesLoading}
            searchString={searchString}
            rowCount={TOTAL_RECORDS_MAPPER[importData.type] ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
          />
        )}
      </Flex>
    </SideSheetDrawer>
  );
};

export default ViewRecordDrawer;
